import { Component, Input, OnInit, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Roles } from '@shared/models/types';
import { ProfileService } from '@shared/services/api/profile.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { ModalService } from '@shared/services/modal.service';
import { LanguageService } from '@shared/services/language.service';
import { catchError, first, forkJoin, map, of, switchMap, take, tap } from 'rxjs';

interface PublicLegalDocumentMeta {
  type: string;
  lang: string;
  htmlContent?: string;
  hasFile: boolean;
}

@Component({
  selector: 'app-modal-personal-data-info',
  templateUrl: './modal-personal-data-info.component.html',
  styleUrl: './modal-personal-data-info.component.scss'
})
export class ModalPersonalDataInfoComponent implements OnInit {
  private _modalservice = inject(ModalService);
  private _profile = inject(ProfileService);
  private _authenticationService = inject(AuthenticationService);
  private _router = inject(Router);
  private _http = inject(HttpClient);
  private _sanitizer = inject(DomSanitizer);
  private _language = inject(LanguageService);
  isLoading = false;
  termsHtml: SafeHtml = '';
  acceptedTerms = false;
  acceptedPrivacy = false;
  acceptedCookies = false;

  private currentLang = 'it';
  // Populated from the "Documenti legali" admin registry; falls back to the bundled
  // static assets below when a slot hasn't been configured yet (or the API call fails).
  private termsMeta?: PublicLegalDocumentMeta;
  private privacyMeta?: PublicLegalDocumentMeta;
  private cookieMeta?: PublicLegalDocumentMeta;

  get allAccepted(): boolean {
    return this.acceptedTerms && this.acceptedPrivacy && this.acceptedCookies;
  }

  get termsPdfUrl(): string {
    if (this.termsMeta?.hasFile) {
      return this.legalDocumentFileUrl('TERMS', this.currentLang);
    }
    return this.currentLang === 'it'
      ? '/assets/terms/ICAM_CondizioniGeneraliUtilizzo_MyICAM.pdf'
      : '/assets/terms/ICAM_CondizioniGeneraliUtilizzo_MyICAM_EN.pdf';
  }

  get privacyPdfUrl(): string {
    if (this.privacyMeta?.hasFile) {
      return this.legalDocumentFileUrl('PRIVACY', this.currentLang);
    }
    return this.currentLang === 'it'
      ? '/assets/terms/ICAM_PrivacyPolicy_MyICAM.pdf'
      : '/assets/terms/ICAM_PrivacyPolicy_MyICAM_EN.pdf';
  }

  get cookiePdfUrl(): string {
    if (this.cookieMeta?.hasFile) {
      return this.legalDocumentFileUrl('COOKIE', this.currentLang);
    }
    return this.currentLang === 'it'
      ? '/assets/terms/ICAM_CookiePolicy_MyICAM.pdf'
      : '/assets/terms/ICAM_CookiePolicy_MyICAM_EN.pdf';
  }

  ngOnInit() {
    this._language.current$
      .pipe(
        tap((lang) => (this.currentLang = lang)),
        switchMap((lang) =>
          forkJoin({
            terms: this.fetchLegalDocumentMeta('TERMS', lang),
            privacy: this.fetchLegalDocumentMeta('PRIVACY', lang),
            cookie: this.fetchLegalDocumentMeta('COOKIE', lang)
          }).pipe(map((metas) => ({ lang, metas })))
        ),
        switchMap(({ lang, metas }) => {
          this.termsMeta = metas.terms;
          this.privacyMeta = metas.privacy;
          this.cookieMeta = metas.cookie;

          if (metas.terms?.htmlContent) {
            return of(metas.terms.htmlContent);
          }
          return this._http.get(`/assets/terms/${lang}.html`, { responseType: 'text' });
        })
      )
      .subscribe((html) => {
        this.termsHtml = this._sanitizer.bypassSecurityTrustHtml(html);
      });
  }

  private fetchLegalDocumentMeta(type: string, lang: string) {
    return this._http
      .get<PublicLegalDocumentMeta>(`${environment.apiBaseUrl}/public/legal-documents`, {
        params: { type, lang }
      })
      .pipe(catchError(() => of(undefined)));
  }

  private legalDocumentFileUrl(type: string, lang: string): string {
    return `${environment.apiBaseUrl}/public/legal-documents/file?type=${type}&lang=${lang}`;
  }

  @Input() showBtns = false;
  @Input() showClose = true;
  @Input() showPdfLinks = false;
  /**
   * Set by the admin preview page (/legal-documents/preview): the modal renders and behaves
   * exactly as it does for a real user, but accepting neither writes the profile nor logs out,
   * so an administrator can review the flow without side effects on their own account.
   */
  @Input() previewMode = false;

  onBtnClick(cmd: 'accept' | 'deny') {
    if (this.previewMode) {
      this._modalservice.close(cmd);
      return;
    }

    if (cmd === 'accept') {
      this.isLoading = true;
      this._profile.profile$
        .pipe(
          take(1),
          switchMap((profile) => {
            profile.termsAndConditions = true;
            return this._profile.save({ termsAndConditions: true }, profile.id);
          }),
          tap((profile) => {
            this.isLoading = false;
            this._modalservice.close();
            if (profile.role === Roles.PARTNER) {
              this._router.navigate(['/final-users']);
            } else {
              this._router.navigate(['/home']);
            }
          })
        )
        .subscribe();
    } else {
      this._authenticationService
        .logout()
        .pipe(first())
        .subscribe(() => {
          this._modalservice.close();
          this._router.navigate(['/login']);
        });
    }
  }

  onCloseModal() {
    this._modalservice.close();
  }
}
